import React from "react";
import styled from "@emotion/styled";
import Link from "next/link";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import Button from "@components/Button";
import VideoModal from "@components/video-modal";
import { CPagesPath } from "shared/models/const/pages-path";
import { useTranslations } from "next-intl";

function VideosThumbnail(props: IVideosThumbnail) {
  const { src, title } = props;
  const translation = useTranslations("join.motivation");

  return (
    <VideoModal src={src} title={title}>
      <VideoTrigger type="button" aria-label={title}>
        <ThumbnailWrapper>
          <PreviewVideo muted playsInline preload="metadata">
            <source src={src} type="video/mp4" />
          </PreviewVideo>
          <PlayOverlay aria-hidden="true">
            <PlayArrowIcon />
          </PlayOverlay>
        </ThumbnailWrapper>
        <VideoText>{translation("seeVideo")}</VideoText>
      </VideoTrigger>
    </VideoModal>
  );
}

const videosItems = [
  {
    src: "/videos/marion-testimonio-v1.mp4",
    title: "Testimonio Marion",
  },
];

function Motivation() {
  const translation = useTranslations("join.motivation");
  return (
    <Wrapper>
      <MotivationContainer className="container">
        <MotivationVideos>
          {videosItems.map((item, index) => (
            <article key={index}>
              <VideosThumbnail {...item} />
            </article>
          ))}
        </MotivationVideos>
        <MotivationDescription>
          <MotivationTitle>{translation("title")}</MotivationTitle>
          <MotivationText>{translation("description")}</MotivationText>
          <MotivationText>{translation("info")}</MotivationText>
          <Link href={CPagesPath.es.frequent_questions} passHref>
            <MotivationButton disableRipple component="a">
              {translation("faq")}
            </MotivationButton>
          </Link>
        </MotivationDescription>
      </MotivationContainer>
    </Wrapper>
  );
}

const Wrapper = styled.section`
  background-color: var(--color-brand-cream-primary);
  padding: 85px 0;
  @media screen and (max-width: 767px) {
    padding: 2rem 1rem;
    * {
      text-align: center;
    }
  }
`;

const MotivationContainer = styled.div`
  display: flex;
  justify-content: space-around;
  @media screen and (max-width: 991px) {
    flex-direction: column;
  }
`;

const MotivationDescription = styled.section`
  max-width: 600px;
  padding: 0px 10px;
  align-self: center;
  @media screen and (max-width: 767px) {
    max-width: 600px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`;

const MotivationTitle = styled.h2`
  font-size: 1.338rem;
  font-weight: 700;
  text-align: left;
  text-transform: none;
  margin-bottom: 24px;
  color: var(--color-brand-blue-main);
  @media screen and (max-width: 767px) {
    text-align: center;
  }
`;

const MotivationText = styled.p`
  font-size: 0.938rem;
  font-weight: 700;
  line-height: 18px;
  color: #99999b;
  margin-bottom: 20px;
`;

const MotivationButton = styled(Button)`
  margin-top: 15px;
  border-radius: 15px;
  height: 40px;
  color: var(--color-brand-bg-secondary);

  @media screen and (max-width: 991px) {
    display: block;
    margin: 35px auto 0;
  }

  & > a {
    color: inherit;
  }

  &:focus-visible {
    outline: Highlight auto 1px;
    outline: -webkit-focus-ring-color auto 1px;
  }
`;

const MotivationVideos = styled.div`
  & > :nth-child(1) {
    // margin-bottom: 45px;
    margin-bottom: 1rem;
  }
  @media screen and (max-width: 991px) {
    display: flex;
    justify-content: space-evenly;
  }
  @media screen and (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    margin-bottom: 1rem;
  }
`;

const VideoTrigger = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: block;
  padding: 0;
`;

const PreviewVideo = styled.video`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const ThumbnailWrapper = styled.div`
  width: 460px;
  aspect-ratio: 16 / 9;
  border-radius: 20px;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  margin-bottom: 15px;
  overflow: hidden;
  position: relative;
  @media screen and (max-width: 480px) {
    width: 100%;
    max-width: 380px;
    margin: auto;
  }
`;

const PlayOverlay = styled.span`
  align-items: center;
  background-color: var(--color-brand-red-main);
  border-radius: 50%;
  color: var(--color-text-white);
  display: flex;
  height: 62px;
  justify-content: center;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 62px;

  svg {
    font-size: 42px;
  }
`;

const VideoText = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  color: var(--color-brand-blue-main);
  cursor: pointer;
`;

interface IVideosThumbnail {
  src: string;
  title: string;
}

export default Motivation;
